import React, { useState, useEffect } from 'react';
import clsx from 'clsx';

interface AccordionProps {
  title: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
}

export const Accordion: React.FC<AccordionProps> = ({ title, children, defaultOpen = false, isOpen: controlledIsOpen, onToggle }) => {
  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen);
  
  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;

  useEffect(() => {
    if (controlledIsOpen !== undefined) {
      setInternalIsOpen(controlledIsOpen);
    }
  }, [controlledIsOpen]);

  const handleToggle = () => {
    const newState = !isOpen;
    setInternalIsOpen(newState);
    onToggle?.(newState);
  };

  return (
    <div className="rounded-2xl overflow-hidden shadow-md hover:shadow-lg transition-shadow">
      <button
        onClick={handleToggle}
        className={clsx(
          'w-full px-5 py-4 flex items-center justify-between',
          'bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900',
          'hover:from-gray-100 hover:to-gray-50 dark:hover:from-gray-700 dark:hover:to-gray-800',
          'transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset',
          'border border-gray-200 dark:border-gray-700'
        )}
        aria-expanded={isOpen}
      >
        <div className="text-left text-base font-bold text-gray-900 dark:text-white flex-1">{title}</div>
        <svg
          className={clsx(
            'w-6 h-6 text-gray-600 dark:text-gray-400 transition-transform ml-4',
            isOpen && 'transform rotate-180'
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="px-6 py-4 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
          {children}
        </div>
      )}
    </div>
  );
};
